import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { timer } from 'rxjs/observable/timer';

import { ApplicationService } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService, InputBoxDialogOptions, OptionBoxDialogOptions, OptionBoxOption } from '../../services/dialogs/dialogs.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';
import { VERSION_NUMBER } from '../../services/version/version.service';
import { IssueAlertInfo } from '../../services/application/application.service';

@Component({
    selector: 'app-login',
    styleUrls: ['login.component.css'],
    templateUrl: 'login.component.html',
})
export class LoginComponent implements OnDestroy, OnInit {
    private _failures: number;
    private _subRelease: Subject<void> = new Subject<void>();

    IssueAlert: IssueAlertInfo;
    LastSizeCode: string;
    LoginInProgress: boolean;
    ShowImpersonationBox: boolean;
    ShowingParent: boolean;
    ShowingUpdate: boolean;
    SmallDisplay: boolean;
    username: string;
    password: string;

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService,
        private router: Router,
        private dialogService: DialogService
    ) {
        const self = this;
        this._failures = 0; // No login failures to start.
        this.IssueAlert = self.appService.IssueAlerts && self.appService.IssueAlerts[0];
        this.LoginInProgress = false;
        this.ShowImpersonationBox = false;
        this.ShowingParent = true;
        this.ShowingUpdate = false;

        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
            ? 'sm'
            : self.mediaChange.isActive('md')
            ? 'md'
            : self.mediaChange.isActive('lg')
            ? 'lg'
            : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subRelease))
            .subscribe((media) => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.LastSizeCode = media.mqAlias;
            });

        // Subscribe to the system online status subject. If it triggers as online, we can load the rest of the page items needed for the page.
        this.authService.SystemOnlineSubject.pipe(takeUntil(this._subRelease)).subscribe((result) => {
            self.appService.IssueAlerts = result && result.Alerts;
            self.IssueAlert = self.appService.IssueAlerts && self.appService.IssueAlerts.length && self.appService.IssueAlerts[0];

            if (result && result.Status) {
                // The system is back online. Reload what's needed for this page.
                if (result && result.DeltaVersion && result.DeltaVersion !== VERSION_NUMBER) {
                    // Different version number. Prompt the user to refresh, but only if we're not currently already
                    // showing that dialog box. No need to stack dialog boxes.
                    if (!self.ShowingUpdate) {
                        self.ShowingUpdate = true;

                        self.dialogService
                            .OpenMessageBoxDialog({
                                TitleText: 'New DELTA Version Available',
                                MessageText:
                                    `The application has determined that a new version of the DELTA System is ` +
                                    `available. New versions typically contain important bug fixes or feature upgrades. In order ` +
                                    `to log in to DELTA, you will need to refresh your browser and load the newest version of ` +
                                    `DELTA. Click the "OK" button to refresh the login screen and continue.`,
                                OKText: 'OK',
                            })
                            .subscribe(() => {
                                location.reload(true);
                            });
                    }
                }
            }
        });

        // Set up a subscription on Test Connection, to check for the system being available while the user is sitting on the landing page.
        timer(0, 60000)
            .pipe(takeUntil(self._subRelease))
            .subscribe((_) => {
                // Call out to test the connection.
                this.authService.TestConnection().subscribe(
                    (result) => {
                        // Update the status.
                        this.authService.SetSystemOnlineStatus(result);
                    },
                    (err) => {
                        this.authService.SetSystemOnlineStatus({
                            Status: false,
                            SystemMessage: '',
                            DeltaVersion: VERSION_NUMBER,
                        });
                    }
                );
            });
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {}

    onSubmit(form: any) {
        // Check for a password. If none present, don't allow.
        if (form.password == null || form.password === '') {
            this.splashService.SnackbarError('Please provide a password.', 5000);

            document.getElementById('password').focus();

            return;
        }

        // Check the username for an '@' symbol. If found, don't allow.
        if (form.username && form.username.indexOf('@') >= 0) {
            this.dialogService
                .OpenMessageBoxDialog({
                    TitleText: 'Username Issue Detected',
                    MessageText:
                        `You appear to be attempting to use an email address as your username. If you are a ` +
                        `parent, student, or college staff, your username is not your email address. Please use your ` +
                        `assigned username to log in to this system.<br /><br /><strong>District Staff:</strong> You do ` +
                        `not need to provide the "@polk-fl.net" portion of your email address for your username.`,
                    OKText: 'OK',
                })
                .subscribe((resp) => {});

            document.getElementById('username').focus();

            return;
        }

        this.LoginInProgress = true;
        this.splashService.DisplaySplash();

        // Grab the screen and window resolutions
        const scr = window.screen.width + 'x' + window.screen.height;
        const win = window.innerWidth + 'x' + window.innerHeight;

        this.authService.ValidateUser(form.username, form.password, form.impersonated, scr, win).subscribe(
            (info: UserInfo) => {
                // Drop the splash that's on the screen.
                this.splashService.CloseSplash();
                this.LoginInProgress = false;

                // Use the user service to process this login, which will handle routing to the proper home page for us.
                this.authService.ProcessLogin(info);
            },
            (error: HttpErrorResponse) => {
                this.splashService.CloseSplash();
                this.LoginInProgress = false;

                const body = error && error.error;
                const msg = body || 'An unknown error has occurred.';

                if (error.status === 401) {
                    // Unauthorized. Typically a failed username/password, but could be a valid login with no roles or attached students.
                    if (body.indexOf('Too many failed attempts within lockout period') >= 0) {
                        // This is a lockout due to too many invalid logins within the lockout period.
                        this.dialogService
                            .OpenMessageBoxDialog({
                                TitleText: 'Too Many Login Attempts',
                                MessageText:
                                    `You have attempted to login to the requested account too many times within ` +
                                    `the designated security lockout period. Any further login attempts to the specified ` +
                                    `account will be denied until the current security lockout time has passed. Access to ` +
                                    `the specified account will be reauthorized after a brief delay. Thank you.`,
                                height: '300px',
                            })
                            .subscribe();
                    } else if (body.indexOf('No attached students or access role.') >= 0) {
                        // This is a successful login, but not one which has a role or attached students.
                        // It does not count as a failure, per se.
                        this.dialogService
                            .OpenMessageBoxDialog({
                                TitleText: 'No Roles For DELTA',
                                MessageText:
                                    `Your login attempt was successful, but the system is unable to provide ` +
                                    `access to the DELTA System for you at the present time because you have no attached ` +
                                    `students in the Focus Parent Portal system and you have no other defined role ` +
                                    `(such as School Counselor, School Administrator, or College Advisor) in the system.` +
                                    `<br /><br />If you are a parent of the Polk County student, you will need to complete ` +
                                    `the student attachment process within the Focus Parent Portal to receive access to the ` +
                                    `DELTA System for your attached children. Please also note that the DELTA System is only ` +
                                    `available for parents of children who are enrolled in the 6th grade or higher.<br /><br />` +
                                    `For more information on setting up your Focus Parent Portal account, please refer to ` +
                                    `our district <a href="https://polkschoolsfl.com/parentportal/" target="_blank">Parent ` +
                                    `Portal Information</a> page. (Link opens in new tab/window.)<br /><br />Thank you for ` +
                                    `your interest in the DELTA System.`,
                                height: '550px',
                            })
                            .subscribe();
                    } else if (
                        body.indexOf('Unable to login as a parent using previous Polk County Public Schools Portal account.') >= 0
                    ) {
                        // This is a successful login, but not one which can be used since it is a parent logging in from an old
                        // parent portal account. We need to gently redirect them to Focus instead.
                        this.dialogService
                            .OpenMessageBoxDialog({
                                TitleText: 'Unable to Login with Discontinued Portal Account',
                                MessageText:
                                    `Your login attempt was successful, but the DELTA system is unable to provide ` +
                                    `you access because your account credentials appear to match our discontinued Polk County ` +
                                    `Public Schools Parent Portal system.<br /><br />In order to access DELTA, you will need ` +
                                    `to log in to the district Focus Parent Portal at the link provided below, and then use ` +
                                    `the "Additional Student Information" menu link to access DELTA. If you do not have a ` +
                                    `Focus Parent Portal account, you may find instructions for creating your new Focus Parent ` +
                                    `Portal account by referring to our district ` +
                                    `<a href="https://polkschoolsfl.com/parentportal/" target="_blank">Parent Portal Information</a> ` +
                                    `page. (Link opens in new tab/window.)<br /><br /><a href="https://focusk12.polk-fl.net/focus/" ` +
                                    `target="_blank" class="anchor"><img src="assets/Images/countylogo.jpg" /><br />` +
                                    `<span>Focus Parent Portal</span></a>`,
                                height: '500px',
                                width: '800px',
                            })
                            .subscribe();
                    } else if (
                        body.indexOf('Unable to login as a student using previous Polk County Public Schools Portal account.') >= 0
                    ) {
                        // This is a successful login, but not one which can be used since it is a parent logging
                        // in from an old parent portal account. We need to gently redirect them to Focus instead.
                        this.dialogService
                            .OpenMessageBoxDialog({
                                TitleText: 'Unable to Login with Discontinued Portal Account',
                                MessageText:
                                    `The DELTA system is unable to provide you access because your account ` +
                                    `credentials appear to match our discontinued Polk County Public Schools Portal system.` +
                                    `<br /><br />In order to access DELTA, you will need to be an active student account ` +
                                    `in our district Active Directory system. Until that account is active, your access ` +
                                    `to DELTA cannot be validated.`,
                                height: '500px',
                                width: '800px',
                            })
                            .subscribe();
                    } else {
                        // This is an unsuccessful login based upon password.
                        this.splashService.SnackbarError(msg, 8000);

                        this._failures++;

                        if (this._failures === 3) {
                            // Time to pop up a choice box because they are still having problems.
                            const options: OptionBoxDialogOptions = {
                                TitleText: 'Problems Logging In?',
                                MessageText:
                                    `You appear to be having some difficulty logging in to the system. ` +
                                    `Do you require any assistance?`,
                                Options: [
                                    {
                                        DisplayText: 'I forgot my password.',
                                        Color: 'warn',
                                        Response: 'password',
                                    },
                                    {
                                        DisplayText: `No - I'll try again`,
                                        Color: 'primary',
                                        Response: 'cancel',
                                    },
                                ],
                            };

                            this.dialogService.OpenOptionBoxDialog(options).subscribe((response) => {
                                // Reset the failures back to 0.
                                this._failures = 0;

                                switch (response) {
                                    case 'username': // Username forgotten. Collect email address and send on.
                                        // this.ForgotUsername();

                                        break;
                                    case 'password': // Password forgotten. Collect email address and send on.
                                        this.dialogService.OpenMessageBoxDialog({
                                            TitleText: 'Getting Password Help',
                                            MessageText:
                                                `To change your password, you can click on the "Need Password Help?" ` +
                                                `link on the login form to go to the Password Reset Tool. This tool can assist ` +
                                                `you in resetting your password.<br /><br /><strong>Note: </strong>This tool ` +
                                                `is only for the use of district staff and students. Parents with Focus password ` +
                                                `issues must use the Focus tools available at the Focus Parent portal in order ` +
                                                `to resolve password issues.`,
                                            OKText: 'OK',
                                        });

                                        break;
                                    case 'cancel': // User wishes to cancel and try again.
                                        break;
                                }
                            });
                        } else {
                        }
                    }
                } else {
                    this.splashService.SnackbarError(msg, 8000);
                }
            }
        );
    }

    // #region Show Impersonation Method

    ShowImpersonation(event: any) {
        if (!event) {
            return;
        }

        if (event.ctrlKey || event.metaKey || event.tapCount === 2) {
            this.ShowImpersonationBox = !this.ShowImpersonationBox;
        }
    }

    // #endregion

    ToggleInfoPanel() {
        this.ShowingParent = !this.ShowingParent;
    }
}



// WEBPACK FOOTER //
// ./src/app/login/login.component.ts